import { HttpApiService } from "@/shared/services/httpApi.service";
import { buildQueryString } from "@/shared/utils/buildQueryString";

import { USERS_API_ENDPOINTS } from "../constants/users.constants";
import type { CreateUserPayload, UpdateUserPayload, UserStatus } from "../types/create-user.types";
import type {
  InternalUserResponse,
  MutatedUserResponse,
  UsersListResponse,
  UserStatusCounts,
} from "../types/users-api.types";

/**
 * Listing filters, all resolved server-side.
 *
 * Every optional param must be spread conditionally by the caller, never assigned as an empty string:
 * `buildQueryString` drops `null`/`undefined` but KEEPS `""`, and the API runs `forbidNonWhitelisted`, so
 * `?status=` is a 400 rather than an ignored param. The UI's `"all"` sentinel must be translated to _absent_
 * for the same reason.
 */
export interface FetchUsersParams {
  skip?: number;
  /** The API caps this at 200 (`PaginationDto.@Max(200)`); it is never clamped here. */
  limit?: number;
  /** 3–100 characters, or the API answers 400. Never send it blank. */
  search?: string;
  status?: UserStatus;
  /** A UUID. The `"all"` sentinel cannot reach this. */
  role_id?: string;
  /** CSV of extras. Today only `"roles"`; an unknown value is a 400, not an ignored param. */
  include?: "roles";
}

export const usersService = {
  fetchList: async (params?: FetchUsersParams): Promise<UsersListResponse> => {
    const response = await HttpApiService.get<UsersListResponse>(
      buildQueryString(USERS_API_ENDPOINTS.USERS, params ?? {}),
    );
    return response.data;
  },

  /**
   * Row counts per status for the SAME filters the listing applies.
   *
   * `status`, `include`, `skip` and `limit` are accepted by the endpoint (the DTO is shared) but ignored — a
   * tile has to report the size of its own bucket — so they are deliberately absent from this signature.
   */
  fetchCounts: async (params: Pick<FetchUsersParams, "search" | "role_id">): Promise<UserStatusCounts> => {
    const response = await HttpApiService.get<UserStatusCounts>(
      buildQueryString(USERS_API_ENDPOINTS.USERS_COUNTS, params),
    );
    return response.data;
  },

  getById: async (id: string): Promise<InternalUserResponse> => {
    const response = await HttpApiService.get<InternalUserResponse>(USERS_API_ENDPOINTS.userById(id));
    return response.data;
  },

  create: async (payload: CreateUserPayload): Promise<MutatedUserResponse> => {
    const response = await HttpApiService.post<MutatedUserResponse, CreateUserPayload>(
      USERS_API_ENDPOINTS.USERS,
      payload,
    );
    return response.data;
  },

  /**
   * Sends the payload verbatim — it is expected to be a DIFF, not the whole entity. Resending unchanged
   * fields is what makes editing a Coloca staff member a 400, because `user_type: "internal"` paired with
   * their root `organization_id` is refused by the service. `UpdateUserPayload` makes both unrepresentable.
   */
  update: async (id: string, payload: UpdateUserPayload): Promise<MutatedUserResponse> => {
    const response = await HttpApiService.patch<MutatedUserResponse, UpdateUserPayload>(
      USERS_API_ENDPOINTS.userById(id),
      payload,
    );
    return response.data;
  },

  /**
   * Stamps `email_verified_at` AND forces `status = active`. There is no idempotency check server-side: on an
   * already-verified user it re-stamps the timestamp and would silently reactivate a suspended account, so
   * only offer it when `email_verified_at` is null.
   */
  verifyEmail: async (id: string): Promise<MutatedUserResponse> => {
    const response = await HttpApiService.post<MutatedUserResponse, Record<string, never>>(
      USERS_API_ENDPOINTS.verifyEmail(id),
      {},
    );
    return response.data;
  },

  /** Soft delete. Revokes every session of the target. 204, empty body. */
  remove: async (id: string): Promise<void> => {
    await HttpApiService.delete(USERS_API_ENDPOINTS.userById(id));
  },
};
